<template>
  <div class="sidenav-content">
    <div
      v-if="open(RouterEnum.CustomerManagement.path)"
      class="sidenav-category"
    >
      <div class="sidenav-category-title">고객관리</div>
      <div class="sidenav-category-items">
        <SidenavItem
          :to="RouterEnum.CustomerManagement.path"
          :active="isActive(RouterEnum.CustomerManagement.path)"
          text="고객 목록"
          icon="customers"
        />
      </div>
    </div>
    <div
      v-if="open(RouterEnum.Subscription.Chatbot.path)"
      class="sidenav-category"
    >
      <div class="sidenav-category-title">구독관리</div>
      <div class="sidenav-category-items">
        <!-- 구독상품 관리: 기본 숨김 (필요 시 v-if 조건 변경) -->
        <SidenavItem
          v-if="false"
          :to="RouterEnum.Subscription.Products.path"
          :active="isActive(RouterEnum.Subscription.Products.path)"
          text="구독상품 관리"
          icon="products"
        />
        <SidenavItem
          :to="RouterEnum.Subscription.Promotions.path"
          :active="isActive(RouterEnum.Subscription.Promotions.path)"
          text="프로모션 관리"
          icon="promotion"
        />
        <SidenavItem
          :to="RouterEnum.Subscription.Chatbot.path"
          :active="isActiveSubscription"
          text="구독관리"
          icon="subscription"
        />
        <SidenavItem
          v-if="open(RouterEnum.Subscription.PaymentHistory.path)"
          :to="RouterEnum.Subscription.PaymentHistory.path"
          :active="isActive(RouterEnum.Subscription.PaymentHistory.path)"
          text="결제내역"
          icon="payment"
        />
      </div>
    </div>
    <div v-if="open(RouterEnum.CallbotUsageFee.path)" class="sidenav-category">
      <div class="sidenav-category-title">콜봇</div>
      <div class="sidenav-category-items">
        <SidenavItem
          :to="RouterEnum.CallbotUsageFee.path"
          :active="isActiveCallbotUsageFee"
          text="콜봇 사용료"
          icon="payment"
        />
      </div>
    </div>
    <div
      v-if="open(RouterEnum.Integration.ChannelManagement.path)"
      class="sidenav-category"
    >
      <div class="sidenav-category-title">연동관리</div>
      <div class="sidenav-category-items">
        <SidenavItem
          :to="RouterEnum.Integration.ChannelManagement.path"
          :active="isActive(RouterEnum.Integration.ChannelManagement.path)"
          text="채널 관리"
          icon="channels"
        />
      </div>
    </div>
    <!-- 직원 계정(리셀러 서브 계정·접근 권한, 11-02) — 「리셀러 직원 계정 관리」 권한이 있을 때만 -->
    <div v-if="open(RouterEnum.Staff.path)" class="sidenav-category">
      <div class="sidenav-category-title">계정관리</div>
      <div class="sidenav-category-items">
        <SidenavItem
          :to="RouterEnum.Staff.path"
          :active="isActive(RouterEnum.Staff.path)"
          text="직원 계정"
          icon="security"
        />
      </div>
    </div>
    <!-- 지원 탭(서티라이프 지원 계정)은 비밀번호·2차 인증을 쓰지 않음(서버도 거절, SUPPORT 4) -->
    <div v-if="!blockedInSupportTab('mySecurity', supportTab)" class="sidenav-category">
      <div class="sidenav-category-title">보안설정</div>
      <div class="sidenav-category-items">
        <SidenavItem
          :to="RouterEnum.Security.ChangePassword.path"
          :active="isActive(RouterEnum.Security.ChangePassword.path)"
          text="암호 변경"
          icon="password"
        />
        <SidenavItem
          :to="RouterEnum.Security.MyAccount.path"
          :active="isActive(RouterEnum.Security.MyAccount.path)"
          text="내 계정 보안"
          icon="security"
        />
        <!-- 옛 이메일 OTP·패스키 설정(옛 2단계 로그인 계정용). 휴대폰·이메일 수단은 「내 계정 보안」 -->
        <SidenavItem
          :to="RouterEnum.TwoFactorAuth.Settings.path"
          :active="isActive(RouterEnum.TwoFactorAuth.Settings.path)"
          text="2단계 인증"
          icon="security"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { useRoute } from "vue-router";
import { RouterEnum } from "@/router/router.enums";
import { isWithinSection } from '@/views/CallbotUsageFee/logic/access';
import SidenavItem from "./SidenavItem.vue";
import { isSupportTab } from "@/utils/tokenStore";
import { blockedInSupportTab } from "@/utils/supportSession";
import { canOpen } from "@/utils/access";
import { loadSupportAccess, myAccess } from "@/utils/supportAccess";

export default defineComponent({
  name: "SidenavList",
  components: {
    SidenavItem,
  },
  setup() {
    const route = useRoute();

    const isActive = (path: string): boolean => {
      // CustomerDetail도 고객 목록 메뉴에 활성화
      if (path === RouterEnum.CustomerManagement.path) {
        return route.path === path || route.path.startsWith("/customers/");
      }
      return route.path === path;
    };

    // 구독관리 - 챗봇/음성봇/센터플로우 모두 활성화
    const isActiveSubscription = computed(() => {
      return (
        route.path.startsWith("/subscriptions/chatbot") ||
        route.path.startsWith("/subscriptions/voicebot") ||
        route.path.startsWith("/subscriptions/centerflow")
      );
    });

    // Callbot usage fee: shown to every signed-in user like the other menus;
    // highlighted on every tab of the section.
    const isActiveCallbotUsageFee = computed(() =>
      isWithinSection(route.path, RouterEnum.CallbotUsageFee.path)
    );

    // 내 권한·담당 고객사로 열 수 있는 메뉴만(11-02). 막기는 서버가 함
    void loadSupportAccess();
    const open = (path: string) => canOpen(path, myAccess);

    return {
      RouterEnum,
      open,
      isActive,
      isActiveSubscription,
      isActiveCallbotUsageFee,
      // 지원 탭인지는 탭이 열릴 때 정해짐(교환 뒤 새로 고침) — 반응형 값이 필요 없음
      supportTab: isSupportTab(),
      blockedInSupportTab,
    };
  },
});
</script>

<style scoped>
/* 메뉴 칸은 머리(회사 이름)와 꼬리(로그아웃) 사이만 차지하고 넘치면 이 칸만 스크롤(로그아웃 칸과 겹치지 않음).
   맥 기본 스크롤 막대는 굴리기 전엔 안 보여 아래 메뉴(「내 계정 보안」·「2단계 인증」)가 없어진 것처럼 보임 →
   막대를 늘 보이게 그림(-webkit- 스타일을 주면 맥 Chrome·Safari 도 막대를 숨기지 않음) */
.sidenav-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 0;
}

.sidenav-content::-webkit-scrollbar {
  width: 6px;
}

.sidenav-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.sidenav-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.sidenav-category {
  margin-bottom: 24px;
}

.sidenav-category-title {
  padding: 8px 24px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.4);
}

.sidenav-category-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
